import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar as CalendarIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RendezVousPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    service: 'diagnostic',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const services = [
    { id: 'diagnostic', name: 'Consultation olfactive' },
    { id: 'personnalisation', name: 'Personnalisation sur-mesure' },
    { id: 'decouverte', name: 'Découverte des collections' },
    { id: 'refill', name: 'Recharge de parfum' },
  ];

  const timeSlots = [
    '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.date || !formData.time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/appointments`, formData);
      toast.success('Rendez-vous enregistré avec succès ! Nous vous contacterons pour confirmer.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        service: 'diagnostic',
        notes: '',
      });
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Erreur lors de la prise de rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-16">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#1E7A6A]/10 rounded-full mb-6">
            <CalendarIcon className="text-[#1E7A6A]" size={40} />
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
            Réservation
          </p>
          <h1
            className="text-5xl md:text-6xl font-light mb-6 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="rendezvous-title"
          >
            Votre consultation olfactive
          </h1>
          <p className="text-[#332724] font-light max-w-2xl mx-auto leading-relaxed text-lg">
            Offrez-vous un moment hors du temps. Lors de votre <strong className="text-[#5C2018]">consultation
            olfactive</strong>, nous prenons le temps de vous écouter, d'explorer vos goûts et vos
            émotions pour vous guider vers les fragrances qui vous ressemblent. Une expérience
            personnalisée, dans le calme et la confiance, au cœur de notre bar à parfums du
            Polygone de Béziers.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]"
          data-testid="appointment-form"
        >
          {/* Personal Info */}
          <div className="mb-8">
            <h3
              className="text-2xl mb-4 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Vos informations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                placeholder="Nom complet *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                required
                data-testid="appointment-name"
              />
              <input
                type="email"
                placeholder="Email *"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                required
                data-testid="appointment-email"
              />
            </div>
            <input
              type="tel"
              placeholder="Téléphone *"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
              required
              data-testid="appointment-phone"
            />
          </div>

          {/* Service Selection */}
          <div className="mb-8">
            <h3
              className="text-2xl mb-4 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Service souhaité
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {services.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, service: service.id })}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    formData.service === service.id
                      ? 'bg-[#1E7A6A] text-[#FDFAF4] border-[#1E7A6A]'
                      : 'bg-white text-[#332724] border-[#B8922A]/20 hover:border-[#1E7A6A]'
                  }`}
                  data-testid={`service-${service.id}`}
                >
                  {service.name}
                </button>
              ))}
            </div>
          </div>

          {/* Date & Time */}
          <div className="mb-8">
            <h3
              className="text-2xl mb-4 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Date et heure
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm uppercase tracking-[0.2em] mb-2 text-[#332724]">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  min={today}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                  required
                  data-testid="appointment-date"
                />
              </div>
              <div>
                <label className="block text-sm uppercase tracking-[0.2em] mb-2 text-[#332724]">
                  Heure *
                </label>
                <select
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                  required
                  data-testid="appointment-time"
                >
                  <option value="">Sélectionnez une heure</option>
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <label className="block text-sm uppercase tracking-[0.2em] mb-2 text-[#332724]">
              Notes ou demandes spéciales
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="4"
              className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
              placeholder="Indiquez vos préférences, questions ou demandes particulières..."
              data-testid="appointment-notes"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary px-8 py-4 rounded-full font-medium uppercase tracking-[0.2em] text-sm disabled:opacity-50"
            data-testid="appointment-submit"
          >
            {loading ? 'Envoi en cours...' : 'Confirmer le rendez-vous'}
          </button>

          <p className="text-xs text-[#332724]/70 text-center mt-4">
            Nous vous contacterons pour confirmer votre rendez-vous dans les plus brefs délais.
          </p>
        </form>
      </div>
    </div>
  );
};

export default RendezVousPage;
